// Pure helpers for RecommendationsStrip / CompatibleCombinationsBrowserModal
// (AHA-6681). Typed port of alpine-client's
// EquipmentCheck/recommendationsStripLogic.js — semantics must stay identical.

import type { PtacPreferencesRecord } from '@alpinehomeair/common';
import type { TonnageRecord } from './tonnage-logic';

// Mirrors system-selector-api's SystemTypeOption (domain/system-scenario-repository).
export type SystemTypeOption = {
  id: number;
  name: string;
  // The type's compatibility codes when served by context.systemTypes
  // (AHA-6681 want-only fallback). Optional: an older API never sends it,
  // and desiredOptions entries don't carry it — absent behaves like [].
  compatibilityCodes?: string[];
};

// Mirrors system-selector-api's ScenarioRecommendation: compatibilityCodes is
// empty for types with no combination-browser equivalent ("not searchable").
export type ScenarioRecommendation = {
  priority: number;
  systemTypeId: number;
  name: string;
  compatibilityCodes: string[];
};

// Full current strip selection, the onSelectionsChange payload in controlled
// mode (AHA-6681 LES-2). refrigerant/age are null (not '') when unset so the
// persisted JSON matches the API's saved shape.
export type RecommendationSelections = {
  wantIds: number[];
  haveId: number | null;
  refrigerant: string | null;
  age: string | null;
  // "Only show recommended system types" preference (AHA-6681 LES-3). The
  // strip never sets it in its own payloads — the step's per-system wrapper
  // owns the checkbox state and merges its flag in before saving.
  useRecommendations?: boolean;
  // Derivation fingerprint the selections were made under (AHA-6681
  // DECISION 1); undefined only against an API that never sent one.
  fingerprint?: string;
  // Virtual have-tile codes + fuel-chip literal (AHA-6681 tile panel).
  // Present on every tile-mode payload (the PATCH replaces the whole
  // record); never set by dropdown-mode payloads.
  virtualCodes?: string[];
  virtualFuel?: string | null;
  // SYSTEM SIZE record (AHA-6681 tonnage): the tile-row pick / derivation
  // snapshot plus sizingPath and the CS filter escape. Omitted (never null)
  // when the system has no record — the ss-api schema rejects null.
  tonnage?: TonnageRecord;
  ptac?: PtacPreferencesRecord;
};

// Mirrors system-selector-api's SavedRecommendationSelections (the READ-side
// parse of the persisted column): an absent key means "never saved, fall
// back to derived"; an explicit null means "user cleared".
export type SavedRecommendationSelections = {
  wantIds: number[];
  haveId?: number | null;
  refrigerant?: string | null;
  age?: string | null;
  // Fingerprint the record was saved under (AHA-6681 DECISION 1); absent =
  // legacy row ("unknown, re-derive" on restore).
  fingerprint?: string;
  // Absent = never persisted (AHA-6681 LES-3); true arms the strip's
  // response-gated auto-check on restore.
  useRecommendations?: boolean;
  // Virtual have-tile codes + fuel-chip literal (AHA-6681 tile panel).
  // Absent = legacy record (haveId-only "Saved pick" read-compat, §F);
  // explicit [] = user cleared / overwrite-reset — stays blank.
  virtualCodes?: string[];
  virtualFuel?: string | null;
  // Latest compatible-combinations summary (AHA-6681 LES-4, two-group dual
  // shape); absent = never checked (or an old flat row the API dropped —
  // the live check refetches and heals it). 'kept' only exists when >=1
  // component was kept at check time. The strip only forwards it via
  // onSavedSelectionsRestore — it never reads it itself.
  lastResults?: {
    fetchedAt: string;
    alternatives: {
      totalCount: number;
      systemTypes: { code: string; label: string; count: number }[];
    };
    kept?: {
      totalCount: number;
      systemTypes: { code: string; label: string; count: number }[];
    };
  };
  // SYSTEM SIZE record (AHA-6681 tonnage); absent = never saved. The API's
  // tolerant read already drops malformed rows, but the GUI re-parses via
  // parseSavedTonnage anyway (defense in depth against an older API).
  tonnage?: TonnageRecord | null;
  ptac?: PtacPreferencesRecord | null;
};

export type RecommendationContext = {
  systemTypes: SystemTypeOption[];
  refrigerantOptions: string[];
  ageOptions: string[];
  derived: {
    currentSystemTypeId: number | null;
    candidateSystemTypeIds: number[];
    refrigerant: string | null;
    // Canonical derivation-input string (AHA-6681 DECISION 1). Optional —
    // an older API response simply never carries it, and the strip then
    // keeps today's dirty-wins reconcile.
    fingerprint?: string;
    // Want-seed union (AHA-6681 batch 4): full-inventory type ∪
    // replacing-subset types, computed API-side (field name kept until the
    // client-twin rename PR). Optional — an older API response simply never
    // carries it, and the seed then falls back to like-for-like.
    replacingWantSystemTypeIds?: number[];
  };
  // Optional — older API responses (or the strip in uncontrolled mode) simply
  // never carry/read it (AHA-6681 LES-2).
  savedSelections?: SavedRecommendationSelections | null;
};

// Mirrors the API's per-want scenario resolution (AHA-6681 multi-select):
// entries arrive in want-selection order; a want absent from the
// availability-filtered desiredOptions has no entry.
export type WantRecommendationGroup = {
  desiredSystemTypeId: number;
  matched: boolean;
  mergedAcrossAgeBuckets?: boolean;
  recommendations: ScenarioRecommendation[];
};

export type RecommendationsResult = {
  desiredOptions: SystemTypeOption[];
  recommendationsByWant?: WantRecommendationGroup[];
  recommendations: ScenarioRecommendation[];
  // True when ANY selected want matched a scenario.
  matched: boolean;
  // Mirrors the API: true when a null ("Not sure") age unioned two or more
  // age buckets server-side for any resolved want (AHA-6681).
  mergedAcrossAgeBuckets?: boolean;
};

// Union of the ranked recommendations' compatibility codes, deduped while
// preserving priority order. Unmapped recommendations (empty
// compatibilityCodes) contribute nothing — they render in the list but are
// excluded from the limiting systemTypeCodes set.
export function extractRecommendedCodes(
  recommendations: ScenarioRecommendation[] | null | undefined
): string[] {
  const codes: string[] = [];
  const seen = new Set<string>();
  for (const rec of recommendations ?? []) {
    for (const code of rec.compatibilityCodes ?? []) {
      if (!seen.has(code)) {
        seen.add(code);
        codes.push(code);
      }
    }
  }
  return codes;
}

// Full eligible-universe code union of a recommendation-context catalog
// (AHA-6796 fuel vocabulary): every type's compatibility codes, deduped in
// catalog order (per-type code order within — the wantOnlyFallbackCodes
// convention). This is the same universe the "Show all (N)" want-tile list
// draws from, mirrored into redux per system so the fuel chip rows can
// offer every fuel with >=1 candidate type — not just the recommended
// bound's fuels. Types without compatibilityCodes (older API) contribute
// nothing; an all-empty catalog returns [].
export function catalogCodeUnion(
  systemTypes: SystemTypeOption[] | null | undefined
): string[] {
  const codes: string[] = [];
  const seen = new Set<string>();
  for (const type of systemTypes ?? []) {
    for (const code of type.compatibilityCodes ?? []) {
      if (!seen.has(code)) {
        seen.add(code);
        codes.push(code);
      }
    }
  }
  return codes;
}

// Want-only fallback bound (AHA-6681): with wants saved but NO derivation
// anchor, the wants resolve through the API's no-current-system mode; when
// that response carries no codes (or fails, or the API predates the mode)
// the degraded searchable bound is the union of the selected want types'
// own compatibility codes, in wantIds order (per-type code order within),
// deduped (the same convention as extractRecommendedCodes). Want ids
// without a systemTypes entry, or whose entry has empty/absent codes
// (unsearchable type, or an older API that never sent the field),
// contribute nothing; [] = nothing searchable and the caller keeps today's
// honest skip.
export function wantOnlyFallbackCodes(
  wantIds: number[],
  systemTypes: SystemTypeOption[] | null | undefined
): string[] {
  const byId = new Map((systemTypes ?? []).map((type) => [type.id, type]));
  const codes: string[] = [];
  const seen = new Set<string>();
  for (const wantId of wantIds) {
    for (const code of byId.get(wantId)?.compatibilityCodes ?? []) {
      if (!seen.has(code)) {
        seen.add(code);
        codes.push(code);
      }
    }
  }
  return codes;
}

// Arming rule for the auto-check when seeding/derivation (not a user
// gesture) produces the want selection (AHA-6681 requirement A): only a
// non-dirty seed that takes the selection from EMPTY to non-empty arms —
// a like-for-like reseed of an already non-empty selection (have change)
// or a dirty reconcile/prune never does. Mirror of alpine-client's
// shouldArmAutoCheckOnSeed (single-want id shape there).
export function shouldArmAutoCheckOnSeed(
  wantDirty: boolean,
  prevWantIds: number[],
  nextWantIds: number[]
): boolean {
  return !wantDirty && prevWantIds.length === 0 && nextWantIds.length > 0;
}

// Toggle-preserving reconciliation for the derived selects (have/refrigerant)
// on a componentsKey-only context refresh (AHA-6681): an untouched select
// follows the fresh derivation, but a user's explicit (dirty) choice sticks
// as long as the refreshed vocabulary still offers it — falling back to the
// derived value, never to a stale invalid one.
export function reconcileDerivedSelection<T>(
  dirty: boolean,
  current: T,
  derived: T,
  validValues: T[]
): T {
  return dirty && validValues.includes(current) ? current : derived;
}

// Default for the multi-select "What you want" list. Once the user has
// explicitly touched "want" (dirty), each selected id is kept as long as the
// prune universe still contains it; only the unavailable ids drop out
// (never the whole selection). The universe defaults to the desiredOptions
// id set (dropdown behavior, unchanged); the tiles variant passes its
// full-catalog canvas union instead (AHA-6681 amendment B) so a manual
// browse-mode pick survives a later have-derivation whose scenario-backed
// desiredOptions exclude it — only garbage ids ever drop there.
// Not dirty: seed with the API's want-seed
// union (full-inventory type ∪ replacing-subset types, AHA-6681 batch 4)
// members that survive the options filter, falling back to the
// like-for-like [haveId] seed when that intersection is empty (old API
// without the seed field, or the seed misses the options), then to the
// derivation's exact-cover candidates ∩ options (AHA-6681 task dd2cf6:
// like-for-like under ambiguity — a kept-only packaged inventory whose
// have is ambiguous, e.g. PT → PTAC+PTHP, has an empty seed union and a
// null haveId but still must seed the wants), else []. The candidates
// fallback is deliberately data-driven (the API's exact-cover candidates,
// never a hardcoded id table) — this is the extensible seam; future
// fan-out rules extend the API's want-seed union instead. When haveId is
// non-null the candidates equal [haveId], so chaining after the
// like-for-like miss is behavior-preserving for unambiguous inventories.
// Reference stability matters: when the reconciled contents are unchanged
// the ORIGINAL currentDesiredIds array is returned — the strip's recs effect
// keys on the array, so a fresh-but-equal reference would refetch forever.
export function computeDefaultDesiredMulti(
  currentSystemTypeId: number | null,
  desiredOptions: SystemTypeOption[] | null | undefined,
  wantDirty: boolean,
  currentDesiredIds: number[],
  seedWantIds: number[] = [],
  pruneIds?: Set<number>,
  candidateSystemTypeIds: number[] = []
): number[] {
  const availableIds = new Set((desiredOptions ?? []).map((o) => o.id));
  let next: number[];
  if (wantDirty) {
    const universe = pruneIds ?? availableIds;
    next = currentDesiredIds.filter((id) => universe.has(id));
  } else {
    next = seedWantIds.filter((id) => availableIds.has(id));
    if (next.length === 0) {
      next =
        currentSystemTypeId != null && availableIds.has(currentSystemTypeId)
          ? [currentSystemTypeId]
          : [];
    }
    if (next.length === 0) {
      next = candidateSystemTypeIds.filter((id) => availableIds.has(id));
    }
  }
  const unchanged =
    next.length === currentDesiredIds.length &&
    next.every((id, index) => id === currentDesiredIds[index]);
  return unchanged ? currentDesiredIds : next;
}

// Collapse a want-id list to the first entry. Used by the tiles restore
// path (before the first recs POST) and by computeDefaultDesiredSingle.
// length ≤ 1 keeps the input reference; when the caller already holds
// exactly [first], that reference is returned.
export function asSingleWantIds(
  wantIds: number[],
  currentDesiredIds: number[] = wantIds
): number[] {
  if (wantIds.length <= 1) return wantIds;
  const first = wantIds[0];
  if (currentDesiredIds.length === 1 && currentDesiredIds[0] === first) {
    return currentDesiredIds;
  }
  return [first];
}

// Tiles-variant want contract: the component picker composes exactly one
// catalog type (wantIds[0]) and handleWantCompose replaces the whole array,
// so extras from the dropdown-era multi-id seed/restore would stay invisible,
// still drive the recs POST, and vanish on the first pick. Collapse to the
// first surviving id (API union order: full-inventory type, then replacing
// subset; candidates keep their given order). Empty stays empty.
// Reference stability matches computeDefaultDesiredMulti: when the collapsed
// selection is already applied, the ORIGINAL currentDesiredIds is returned
// so the strip's recs effect does not refetch forever.
export function computeDefaultDesiredSingle(
  currentSystemTypeId: number | null,
  desiredOptions: SystemTypeOption[] | null | undefined,
  wantDirty: boolean,
  currentDesiredIds: number[],
  seedWantIds: number[] = [],
  pruneIds?: Set<number>,
  candidateSystemTypeIds: number[] = []
): number[] {
  const next = computeDefaultDesiredMulti(
    currentSystemTypeId,
    desiredOptions,
    wantDirty,
    currentDesiredIds,
    seedWantIds,
    pruneIds,
    candidateSystemTypeIds
  );
  return asSingleWantIds(next, currentDesiredIds);
}

// Flat concat of the per-want rec lists in group (= want-selection) order.
// Feeding the result to extractRecommendedCodes gives the deduped union in
// "want order, then priority order within a want" — the GUI computes the
// limiting codes from the same groups it renders so the two can't drift.
export function flattenWantRecommendations(
  groups: WantRecommendationGroup[] | null | undefined
): ScenarioRecommendation[] {
  return (groups ?? []).flatMap((group) => group.recommendations ?? []);
}

// Carries a narrowed recommended-subset across a recommendedCodes refresh by
// intersecting it with the new set, instead of clearing it outright (clearing
// is the last resort). Normalization: an empty intersection, or one that no
// longer narrows (equals the full new set), collapses to [] = ALL recommended.
// Reference stability matters: when nothing changes, the ORIGINAL prevSubset
// array is returned — resolveEffectiveSystemTypeCodes returns the subset
// array itself and the combinations-fetch effect keys on it, so an identical
// reference lets the setState bail-out prevent a spurious refetch.
export function reconcileRecommendedSubset(
  prevSubset: string[],
  newCodes: string[]
): string[] {
  if (prevSubset.length === 0) return prevSubset;
  const codeSet = new Set(newCodes);
  const survivors = prevSubset.filter((code) => codeSet.has(code));
  if (survivors.length === 0 || survivors.length === codeSet.size) return [];
  return survivors.length === prevSubset.length ? prevSubset : survivors;
}

// Reconciles the recommended-subset against the CURRENT universe facet
// (metadata.systemTypes of the latest combinations response): codes ABSENT
// from the facet are pruned; nothing survives → [] (= ALL recommended, the
// same convention as reconcileRecommendedSubset). This closes the CCB
// 0-results trap: a subset code selected in one universe can vanish from the
// next (kept-set change, activeOnly flip, spec filters), leaving the query
// filtered on a type with zero combinations while its chip doesn't render.
//
// Prune rule is ABSENT-only, deliberately NOT count===0: present-with-zero
// entries still render as visible selected '(0)' chips, and universe-level
// emptiness always manifests as absence anyway (the server's metadata query
// GROUPs over real rows, so a type with no combinations has no facet entry).
//
// Also deliberately NO 'no-longer-narrows' collapse (unlike
// reconcileRecommendedSubset): the facet is a per-response view, not the
// recommendation vocabulary, so a subset that happens to cover every
// facet-present recommended code must keep narrowing — collapsing it would
// silently widen the query when the absent codes return in a later universe.
//
// Reference stability matters (same contract as reconcileRecommendedSubset):
// when nothing is pruned the ORIGINAL prevSubset array is returned, so the
// caller's setState bails out and the combinations-fetch effect (keyed on
// the subset via resolveEffectiveSystemTypeCodes) does not refetch.
export function reconcileSubsetAgainstFacet(
  prevSubset: string[],
  facetSystemTypes: Array<{ code: string; count: number }>
): string[] {
  if (prevSubset.length === 0) return prevSubset;
  const present = new Set(facetSystemTypes.map((st) => st.code));
  const survivors = prevSubset.filter((code) => present.has(code));
  if (survivors.length === 0) return [];
  return survivors.length === prevSubset.length ? prevSubset : survivors;
}

// Q6 "replaces and disables": while the checkbox is on (and codes exist) the
// recommended set wins; the manual selection is left untouched so unchecking
// restores it exactly. `recommendedSubset` narrows within the recommended set
// (the interactive button row while checked); empty subset = ALL recommended
// — the same convention as the manual row's `systemTypeCodes=[]` = All Types,
// and it guarantees the result never empties while checked with codes.
// Every branch returns one of the input arrays unchanged (reference
// stability — callers key fetch effects on the result).
export function resolveEffectiveSystemTypeCodes(
  useRecommendations: boolean,
  recommendedCodes: string[],
  recommendedSubset: string[],
  manualSystemTypeCodes: string[]
): string[] {
  if (!useRecommendations || recommendedCodes.length === 0) {
    return manualSystemTypeCodes;
  }
  return recommendedSubset.length > 0 ? recommendedSubset : recommendedCodes;
}

// Stable empty result for the no-bound case below: reference stability is
// part of this helper's contract, so the null branch must not mint a fresh
// array per call.
const NO_EFFECTIVE_CODES: string[] = [];

// Effective per-system codes for the Choose Systems ducted fetches
// (AHA-6681 requirement D, wants-gateway in sub-build 3). The recommended
// set is the only fetch universe now: boundedCodes null (no summary — no
// bound resolvable) means there is NOTHING to fetch, never a browse-
// everything pass-through. Bounded: the chips narrow WITHIN the bounded
// set; a disjoint chip selection collapses to the FULL bounded set (the
// reconcileRecommendedSubset convention — an empty intersection must never
// widen beyond the recommended set). A bounded-empty set ([]) passes
// through so callers can skip the fetch (nothing is recommended — record
// skipped, don't search everything).
// Reference stability matters: every branch returns one of the input arrays
// (or the shared empty) unchanged — callers key fetch effects on
// content-derived strings, but the stable reference keeps memo consumers
// cheap.
export function resolveDuctedEffectiveCodes(
  chipCodes: string[],
  boundedCodes: string[] | null
): string[] {
  if (boundedCodes === null) return NO_EFFECTIVE_CODES;
  const bounded = new Set(boundedCodes);
  const survivors = chipCodes.filter((code) => bounded.has(code));
  if (survivors.length === 0) return boundedCodes;
  return survivors.length === chipCodes.length ? chipCodes : survivors;
}

// Effective codes for a strip recommendations report (AHA-6681 want-only
// fallback): the recommended set wins when non-empty; otherwise the report's
// wantOnlyCodes (only the strip's no-anchor reports carry them — the
// no-current-system response and its dead-end/failure paths) bound the
// check instead of skipping it. An anchored zero (matched with
// recommendedCodes: []) carries no wantOnlyCodes and deliberately resolves
// to [] — the honest zero. Shared by the wrapper's debounced refetch, the
// page's Next-click safety net and the Choose Systems healer so their
// runCompatibilityCheck params can never drift (the thunk dedupes on them).
// Every branch returns an input array (or the shared empty) unchanged — the
// file's reference-stability convention.
export function resolveEffectiveRecommendedCodes(
  recommendedCodes: string[],
  wantOnlyCodes?: string[]
): string[] {
  return recommendedCodes.length > 0
    ? recommendedCodes
    : wantOnlyCodes ?? NO_EFFECTIVE_CODES;
}

// Sum of the facet counts for the recommended types only — the honest
// "All Types" number while the checkbox is on. Recommended codes absent from
// the facet (zero combinations in the anchor's universe) contribute 0.
export function recommendedAllTypesCount(
  availableSystemTypes: Array<{ code: string; count: number }>,
  recommendedCodes: string[]
): number {
  const recommended = new Set(recommendedCodes);
  return availableSystemTypes
    .filter((st) => recommended.has(st.code))
    .reduce((sum, st) => sum + (st.count ?? 0), 0);
}

// Checkbox is unusable (and must be force-unchecked by the caller) when no
// scenario matched or every recommended type is unmapped.
export function isRecommendationsCheckboxDisabled(
  matched: boolean,
  recommendedCodes: string[]
): boolean {
  return !matched || recommendedCodes.length === 0;
}

// Floats the derivation's candidate system types to the top of the "What you
// have" select so an ambiguous derivation still guides the user. Preserves
// the incoming (name-sorted) order within each partition.
export function orderSystemTypeOptions(
  systemTypes: SystemTypeOption[] | null | undefined,
  candidateSystemTypeIds: number[] | null | undefined
): SystemTypeOption[] {
  const types = systemTypes ?? [];
  if (!candidateSystemTypeIds?.length) return types;
  const candidateSet = new Set(candidateSystemTypeIds);
  return [
    ...types.filter((t) => candidateSet.has(t.id)),
    ...types.filter((t) => !candidateSet.has(t.id))
  ];
}

// Blank-as-union anchor (AHA-6681 DECISION 2): the recommendations fetch and
// the want checkboxes are usable with an explicit "have" OR a blank one whose
// derivation floated candidates — the fetch then unions across the candidates.
// Only "no have AND no candidates" is the dead-end. A blank includes the
// user-cleared one (uniform "blank = union"); only a non-null pick is
// single-type.
export function hasRecommendationAnchor(
  haveId: number | null,
  candidateSystemTypeIds: number[] | null | undefined
): boolean {
  return haveId != null || (candidateSystemTypeIds?.length ?? 0) > 0;
}

// Session-ownership gate for the recommendations fetch (AHA-6681 stale
// cross-session race): the context effect bails on a null systemUniqueId
// without clearing context, so a swap (old id → new id) re-runs the recs
// effect with the OLD context paired with the NEW id before the context
// refetch settles. The exposed host is the CCB modal's swap-on-reopen: the
// debug menu snapshots existingSystems[0].uniqueId into fetchParams at
// click time and never clears it, so the mounted strip's id swaps A→B on
// reopen after a reseed — the stale POST would settle AFTER the modal's
// render-adjust already reset its codes/checkbox for the session change,
// repopulating them with the dead session's codes. Only a context whose
// owning id equals the CURRENT non-null id may fetch; `systemUniqueId !=
// null` is explicit so a discard window (both ids null) is never a match —
// the discard half can't occur here (the prop is typed string and the
// strip is conditionally unmounted), but the term keeps this twin
// semantically identical to alpine-client's and covers it structurally.
export function isContextForSession(
  contextSystemId: string | null,
  systemUniqueId: string | null | undefined
): boolean {
  return systemUniqueId != null && contextSystemId === systemUniqueId;
}

// Placeholder for the (disabled) want-select. With an anchor (explicit have,
// or blank-with-candidates union — AHA-6681 DECISION 2) but zero desired
// options, wantId is null so the bottom "No recommendations…" note never
// renders — this placeholder is the only dead-end signal (AHA-6681).
export function desiredSelectPlaceholder(
  haveId: number | null,
  desiredOptions: SystemTypeOption[] | null | undefined,
  candidateSystemTypeIds?: number[] | null
): string {
  return hasRecommendationAnchor(haveId, candidateSystemTypeIds) &&
    (desiredOptions ?? []).length === 0
    ? 'No recommendations for this combination'
    : 'Select a system type';
}

// Gate for the CCB modal's combinations fetch (AHA-6681 stale-codes race):
// the strip only reports recommendedCodes AFTER its recommendations POST
// lands, so a combinations fetch fired during the in-flight window would
// pair fresh deps (kept set, inputs, page/sort) with the OUTGOING code set —
// e.g. a kept toggle re-runs the modal's fetch effect with the new kept ids
// but the old codes. Defer only while a refetch is in flight AND the
// checkbox actually narrows the search; the manual-pills path never waits
// on recommendations.
export function shouldDeferCombinationsFetch(
  useRecommendations: boolean,
  recommendationsPending: boolean
): boolean {
  return !!(useRecommendations && recommendationsPending);
}

// How a recommendations report settles a pending window:
// 'refetch' — a combinations fetch was deferred during the window, force
//             one now (its deps may not move again on their own — e.g. the
//             kept toggle that opened the window already consumed the dep
//             change, and identical codes keep the recommendedCodes
//             reference — and effectiveSystemTypeCodes with it — still);
// 'clear'   — pending but nothing was deferred (display-only settle, e.g.
//             an age change whose codes came back identical);
// 'none'    — no window open (ordinary report).
export function resolveRecommendationsSettle(
  pending: boolean,
  deferred: boolean
): 'none' | 'clear' | 'refetch' {
  if (!pending) return 'none';
  return deferred ? 'refetch' : 'clear';
}

// All equipment replacing (no kept anchors) AND no effective system-type
// codes: the backend guards this to an empty result (an unanchored search
// needs a type anchor), so the browser modal skips the fetch and shows an
// explanatory empty state instead.
export function isUnanchoredWithoutCodes(
  keptComponentUniqueIds: string[],
  effectiveSystemTypeCodes: string[]
): boolean {
  return (
    keptComponentUniqueIds.length === 0 && effectiveSystemTypeCodes.length === 0
  );
}

// Stable string key for "the component set changed" — includes the replacing
// flag because refrigerant precedence prefers replacing components.
// `specsVersion` is a caller-owned nonce bumped after a spec edit PERSISTS
// (CCB in-card save): the strip's context POST re-reads the DB, so keying on
// Redux spec values would risk anchoring stale data — the nonce fires exactly
// when the DB is guaranteed current.
export function buildComponentsKey(
  components:
    | Array<{
        uniqueId: string;
        replacing: boolean;
      }>
    | null
    | undefined,
  specsVersion: number = 0
): string {
  const base = (components ?? [])
    .map((c) => `${c.uniqueId}:${c.replacing ? 1 : 0}`)
    .sort()
    .join(',');
  return specsVersion === 0 ? base : `${base}|v${specsVersion}`;
}
